import { type ReactNode } from 'react'
import { type CardThemeTokens, type Review } from '@terros/common'
import { Group, Paper, Stack, Text } from '@mantine/core'
import { ColorPrimitives, Theme, useTranslation } from '@front/model'
import { MaterialSymbolIcon } from '@components'
import { PreviewReview } from './PreviewReview'

const SAMPLE_REVIEW_COUNT = 128

type Props = {
  hasGoogleBusinessProfile: boolean
  rating: number | undefined
  reviewCount: number | undefined
  review: Review | undefined
  tokens: CardThemeTokens
}

export const PreviewReviews = ({ hasGoogleBusinessProfile, rating, reviewCount, review, tokens }: Props): ReactNode => {
  const { t } = useTranslation()
  const count = reviewCount ?? SAMPLE_REVIEW_COUNT
  const reviewHeading =
    hasGoogleBusinessProfile && count > 0 ? t('Reviews ({{total}})', { total: count }) : t('Reviews')

  return (
    <Stack gap='sm'>
      <Group align='flex-start' justify='space-between'>
        <Stack gap={0}>
          <Text c={tokens.ink} fw='bold' size='lg'>
            {reviewHeading}
          </Text>
          {hasGoogleBusinessProfile && (
            <Group c={tokens.ink} gap='xs'>
              <MaterialSymbolIcon color={tokens.accentOnLight} filled icon='star' size='md' />
              <Text className='font-semibold'>{rating ?? 4.9}</Text>
            </Group>
          )}
        </Stack>
        {hasGoogleBusinessProfile && (
          <Paper bg={ColorPrimitives.white['1000']} c={tokens.ink} px='md' py='xs' radius='full' shadow='xs'>
            <Text fw={600} size='sm'>
              {t('View more')}
            </Text>
          </Paper>
        )}
      </Group>
      <Paper bg={ColorPrimitives.white['1000']} p='lg' radius='xl' shadow='xs'>
        {hasGoogleBusinessProfile ? <PreviewReview review={review} tokens={tokens} /> : <EmptyReviews />}
      </Paper>
    </Stack>
  )
}

const EmptyReviews = (): ReactNode => {
  const { t } = useTranslation()
  return (
    <Text c={Theme.light.text.neutral.secondary}>{t('No reviews yet - be the first to share your experience.')}</Text>
  )
}
